/**
 * FullCalendar day-column header for self-schedule. Renders the date
 * label + a Kula Checkbox that toggles duration-sized slot
 * auto-generation for that day. Disabled / tooltip wording depends
 * on context:
 *
 *   no interviewer  → "Pick an interviewer to select slots"
 *   past day        → "Cannot select slots in the past"
 *   no availability → "Interviewer is not available on this day"
 *   otherwise       → enabled
 *
 * Tooltip is scoped to the checkbox only — hovering the date label
 * does NOT fire the tooltip. Disabled styling (`opacity-50` +
 * `cursor-not-allowed` from `checkboxVariants`) is preserved because
 * we wrap the checkbox in a span that captures hover on behalf of
 * the disabled input.
 */
import { Box, Flex } from '@chakra-ui/react';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components/text';

interface DayCheckboxHeaderProps {
  date: Date;
  isChecked: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  onToggle: (_checked: boolean) => void;
}

export function DayCheckboxHeader({
  date,
  isChecked,
  isDisabled,
  disabledReason,
  onToggle,
}: DayCheckboxHeaderProps) {
  const m = moment(date);
  const isToday = m.isSame(moment(), 'day');

  const checkboxEl = (
    <Checkbox
      size="sm"
      checked={isChecked}
      disabled={isDisabled}
      onChange={(e) => onToggle(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  // Disabled + has-reason → wrap the checkbox (and only the
  // checkbox) with the tooltip. The Box acts as the hover shim so
  // Radix picks up pointer events even though `pointer-events-none`
  // is applied by `checkboxVariants`.
  const checkboxWithTooltip =
    isDisabled && disabledReason ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Box as="span" display="inline-flex" lineHeight={0}>
              {checkboxEl}
            </Box>
          </TooltipTrigger>
          <TooltipContent side="top">{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      checkboxEl
    );

  return (
    <Flex
      direction="row"
      align="flex-start"
      justify="center"
      gap="10px"
      py="10px"
      px="8px"
      color={isToday ? 'primary-500' : 'secondary-700'}
      w="100%"
      h="100%"
    >
      <Flex direction="column" align="flex-start" lineHeight="1.1">
        <KText fontSize="11px" fontWeight="500" textTransform="uppercase">
          {m.format('ddd')}
        </KText>
        <KText fontSize="18px" fontWeight="600">
          {m.format('D')}
        </KText>
      </Flex>
      {checkboxWithTooltip}
    </Flex>
  );
}
